import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAllDataUsers } from "../../../api/user";
import { EnumUserType, enumsectionToI18nKey } from "../../../generated/enums";
import { AllDataAccount, AllDataAccountList } from "../../../generated/types";
import { useAccountStorage } from "../../../hooks/useAccountStorage";
import { useAdvancedSearch } from "../../../hooks/useAdvancedSearch";
import { CSVField, downloadCSV } from "../../../utils/csv";
import {
  clampPage,
  Pagination,
  TABLE_PAGE_SIZE,
} from "../../common/Pagination";
import { compareText, DataTableSort, nextSort } from "../../common/DataTable";
import { EmptyState } from "../../common/EmptyState";
import { TableFilters } from "../../common/TableFilters";
import { Header } from "../../Header";
import { sectionToIcon } from "../../type/default";
import { CreateAccountModal } from "./CreateAccountModal";
import { UserMobileCard } from "./UserMobileCard";
import { UsersSearchHelpModal } from "./UsersSearchHelpModal";
import { EntityCardSkeletons } from "../../common/EntityCard";
import { MobileSort, sortOptions } from "../../common/MobileSort";
import { USER_SORT_VALUES, UserColumnKey, useUserColumns } from "./UserRow";
import { UsersTableContent } from "./UsersTableContent";
import { UserTicket } from "./UserTicket";
import { UserTypeFilter } from "./UserTypeFilter";
import { TICKET_SIDE, TicketColumn } from "../../common/TableDetails";

export const UsersTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<DataTableSort<UserColumnKey>>();
  const [filterType, setFilterType] = useState<EnumUserType | "">("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearchHelpOpen, setIsSearchHelpOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AllDataAccount | null>(null);
  const [users, setUsers] = useState<AllDataAccountList | []>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem("userFavorites");
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error("Error reading favorites from storage", e);
      return [];
    }
  });
  const { t } = useTranslation();
  const { getAccountStorage } = useAccountStorage();
  const accountStorage = getAccountStorage();
  const canSeeCount =
    accountStorage?.type === "admin" || accountStorage?.type === "psc";

  // Filtrer d'abord par type d'utilisateur
  const filteredByType = users.filter((user) => {
    if (filterType === "") return true;
    return filterType ? user.account.type === filterType : true;
  });

  // Appliquer ensuite la recherche avancée
  const getSearchableFields = (user: AllDataAccount) => {
    return [
      user.account.type,
      user.company.company_name,
      user.company.country,
      user.company.city,
      user.company.zip_code,
      user.account.last_name,
      user.account.first_name,
      user.account.email,
      user.account.phone_mobile,
      user.account.phone_fixe,
      user.account.creation_date,
      String(user.account.is_activated || ""),
    ];
  };

  const filteredUsers = useAdvancedSearch({
    searchTerm,
    data: filteredByType,
    fields: getSearchableFields,
  });

  // Les favoris restent en tête ; le tri de colonne s'applique à l'intérieur de chaque groupe.
  const sortedUsers = useMemo(() => {
    const favoritesSet = new Set(favorites);
    const isFav = (u: AllDataAccount) => favoritesSet.has(String(u.account.id));
    const sortValue = sort ? USER_SORT_VALUES[sort.key] : undefined;
    return [...filteredUsers].sort((a, b) => {
      if (isFav(a) !== isFav(b)) return isFav(a) ? -1 : 1;
      if (!sort || !sortValue) return 0;
      const order = compareText(sortValue(a), sortValue(b));
      return sort.direction === "asc" ? order : -order;
    });
  }, [filteredUsers, favorites, sort]);

  const currentPage = clampPage(page, TABLE_PAGE_SIZE, sortedUsers.length);
  const pagedUsers = sortedUsers.slice(
    currentPage * TABLE_PAGE_SIZE,
    (currentPage + 1) * TABLE_PAGE_SIZE
  );

  // Un nouveau critère repart de la première page.
  const changeSearch = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };
  const changeType = (value: EnumUserType | "") => {
    setFilterType(value);
    setPage(0);
  };
  const applySort = (next?: DataTableSort<UserColumnKey>) => {
    setSort(next);
    setPage(0);
  };
  const mobileSortOptions = sortOptions(useUserColumns());
  const changeSort = (key: UserColumnKey) => {
    setSort((current) => nextSort(current, key));
    setPage(0);
  };
  const scrollKey = [
    currentPage,
    searchTerm,
    filterType,
    sort?.key ?? "",
    sort?.direction ?? "",
  ].join("|");

  const refreshUsers = async () => {
    const { users: fetchedAllDataUsers, error: fetchError } =
      await fetchAllDataUsers();
    setUsers(fetchedAllDataUsers);
    setError(fetchError);

    // Mettre à jour l'utilisateur sélectionné si nécessaire
    if (selectedUser && Array.isArray(fetchedAllDataUsers)) {
      const updated = fetchedAllDataUsers.find(
        (u) => u.account.id === selectedUser.account.id
      );
      if (updated) {
        setSelectedUser(updated);
      }
    }
  };

  useEffect(() => {
    const loadUsers = async () => {
      const { users: fetchedAllDataUsers, error: fetchError } =
        await fetchAllDataUsers();
      setUsers(fetchedAllDataUsers);
      setError(fetchError);
      setIsLoading(false);
    };

    loadUsers();
  }, []);

  const handleDownload = () => {
    const fields: CSVField<AllDataAccount>[] = [
      {
        header: "Type de compte",
        key: (user) => user.account.type,
      },
      {
        header: "Entreprise",
        key: (user) => user.company.company_name,
      },
      {
        header: "Nom",
        key: (user) => user.account.last_name,
      },
      {
        header: "Prénom",
        key: (user) => user.account.first_name,
      },
      {
        header: "Email",
        key: (user) => user.account.email,
      },
      {
        header: "Téléphone",
        key: (user) => user.account.phone_mobile,
      },
      {
        header: "Date de création",
        key: (user) => user.account.creation_date,
      },
      {
        header: "Nationalité",
        key: (user) => user.company.country,
      },
    ];

    downloadCSV(filteredUsers, fields, `users`);
  };

  const toggleFavorite =
    (accountId: string | number) => (e: React.MouseEvent) => {
      e.stopPropagation();
      const id = String(accountId);
      setFavorites((prev) => {
        const isFav = prev.includes(id);
        const next = isFav ? prev.filter((fav) => fav !== id) : [...prev, id];
        localStorage.setItem("userFavorites", JSON.stringify(next));
        return next;
      });
    };

  if (error) {
    return (
      <div className="p-4">
        <Header
          title={t(enumsectionToI18nKey["section_accounts"])}
          icon={sectionToIcon.section_accounts}
        />
        <p className="mt-6 text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="w-screen md:w-full flex lg:overflow-hidden">
      <div
        className={`w-full ${TICKET_SIDE} ${
          selectedUser ? "lg:overflow-x-auto" : ""
        }`}
      >
        <div className="p-4">
          <Header
            title={t(enumsectionToI18nKey["section_accounts"])}
            icon={sectionToIcon.section_accounts}
            buttonText={t("login-createAccount")}
            onButtonClick={() => setIsModalOpen(true)}
          />
          <div className="mt-3 rounded-lg">
            <TableFilters
              filterValue={String(filterType)}
              onFilterChange={(value) => changeType(value as EnumUserType)}
              filterOptions={[]}
              searchValue={searchTerm}
              onSearchChange={changeSearch}
              onDownload={handleDownload}
              showResultCount={canSeeCount}
              resultCount={sortedUsers.length}
              onHelpClick={() => setIsSearchHelpOpen(true)}
              customFilter={
                <UserTypeFilter value={filterType} onChange={changeType} />
              }
            />
            {!isLoading && sortedUsers.length === 0 ? (
              <EmptyState
                icon={sectionToIcon.section_accounts}
                title={t("table-no-result")}
              />
            ) : (
              <>
                <div className="md:hidden space-y-4">
                  <MobileSort
                    options={mobileSortOptions}
                    sort={sort}
                    onChange={applySort}
                  />
                  {isLoading && <EntityCardSkeletons rows={2} />}
                  {pagedUsers
                    .filter((d) => !!d?.account)
                    .map((d) => {
                      const id = d.account.id;
                      const isFav = !!id && favorites.includes(String(id));
                      return (
                        <UserMobileCard
                          key={id || String(d.account?.id)}
                          data={d}
                          isFavorite={isFav}
                          onToggleFavorite={toggleFavorite(id)}
                          onSelect={() => setSelectedUser(d)}
                        />
                      );
                    })}
                </div>

                <div className="hidden md:block">
                  <UsersTableContent
                    users={pagedUsers}
                    isLoading={isLoading}
                    favorites={favorites}
                    selectedId={
                      selectedUser ? String(selectedUser.account.id) : ""
                    }
                    sort={sort}
                    onSort={changeSort}
                    scrollKey={scrollKey}
                    onSelectUser={setSelectedUser}
                    onToggleFavorite={toggleFavorite}
                  />
                </div>
                {!isLoading && (
                  <Pagination
                    page={currentPage}
                    pageSize={TABLE_PAGE_SIZE}
                    total={sortedUsers.length}
                    onPageChange={setPage}
                    showTotal={canSeeCount}
                  />
                )}
              </>
            )}
          </div>

          <CreateAccountModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        </div>
      </div>

      <TicketColumn open={!!selectedUser}>
        {selectedUser && (
          <UserTicket
            key={selectedUser.account.id}
            account={selectedUser.account}
            onClose={() => setSelectedUser(null)}
            useBackIcon={false}
            onDataRefresh={refreshUsers}
          />
        )}
      </TicketColumn>
      <UsersSearchHelpModal
        isOpen={isSearchHelpOpen}
        onClose={() => setIsSearchHelpOpen(false)}
      />
    </div>
  );
};
